import { createSlice } from '@reduxjs/toolkit'

// Generates a filters slice with identical reducer logic. Each caller supplies its own
// `name` (unique store key) and `initialFilters`/`extraInitialState` so QA, HR,
// Candidates etc. can have different defaults without duplicating code.
//
// Filter model:
//   - `draft`   — what the user is editing in the filter panel. Editing it does NOT run
//                 the query; nothing takes effect until `applyFilters` is dispatched.
//   - `applied` — the last submitted filters; this is what actually drives the results.
//   - `status`, `sortConfig`, `currentPage`, `itemsPerPage` — IMMEDIATE controls (stage
//     tab, column sort, pagination). They update the query straight away and are not part
//     of the Apply batch.
export const createFiltersSlice = ({ name, initialFilters, extraInitialState = {} }) => {

    const emptyGroup = () => ({
        filters: { ...initialFilters },
        requestorIds: [],
        latestQaActionBy: [],
        companyId: '',
    })

    // Shallow-clone a filter group out of an Immer draft into a plain snapshot.
    const cloneGroup = (group) => ({
        filters: { ...group.filters },
        requestorIds: [...group.requestorIds],
        latestQaActionBy: [...(group.latestQaActionBy || [])],
        companyId: group.companyId,
    })

    const initialState = {
        draft: emptyGroup(),
        applied: emptyGroup(),
        status: [],
        sortConfig: { key: 'createdAt', direction: 'desc' },
        currentPage: 1,
        itemsPerPage: 10,
        hasAppliedDefaultRequestor: false,
        ...extraInitialState,
    }

    const slice = createSlice({
        name,
        initialState,
        reducers: {
            // --- Draft (behind Apply) ---
            setDraftFilter: (state, action) => {
                state.draft.filters[action.payload.key] = action.payload.value
            },
            setDraftRequestorIds: (state, action) => {
                state.draft.requestorIds = action.payload
            },
            setDraftLatestQaActionBy: (state, action) => {
                state.draft.latestQaActionBy = action.payload
            },
            setDraftCompanyId: (state, action) => {
                state.draft.companyId = action.payload
            },
            // Commit the draft — this is the only thing that changes what the list shows
            // (other than the immediate controls). Reset to page 1 on a new filter set.
            applyFilters: (state) => {
                state.applied = cloneGroup(state.draft)
                state.currentPage = 1
            },
            // Discard unapplied edits: snap the draft back to what is currently applied.
            resetDraft: (state) => {
                state.draft = cloneGroup(state.applied)
            },
            // HR's "requested by me" default — applied to BOTH sides so it takes effect
            // immediately without the user having to click Apply.
            applyDefaultRequestor: (state, action) => {
                state.draft.requestorIds = [action.payload]
                state.applied.requestorIds = [action.payload]
                state.hasAppliedDefaultRequestor = true
            },
            clearFilters: (state) => {
                state.draft = emptyGroup()
                state.applied = emptyGroup()
                state.status = []
                state.currentPage = 1
            },

            // --- Immediate controls (take effect at once) ---
            setStatus: (state, action) => {
                const value = action.payload

                if (value === '') {
                    state.status = []
                } else {
                    const exists = state.status.includes(value)

                    if (exists) {
                        state.status = state.status.filter(item => item !== value)
                    } else {
                        state.status.push(value)
                    }
                }

                state.currentPage = 1
            },
            setSortConfig: (state, action) => {
                state.sortConfig = action.payload
                state.currentPage = 1
            },
            setCurrentPage: (state, action) => {
                state.currentPage = action.payload
            },
            setItemsPerPage: (state, action) => {
                state.itemsPerPage = action.payload
                state.currentPage = 1
            },
        },
    })

    return slice
}
